/**
 * Compilador de concessões: `Concessao` declarada vira regra CASL (T51, CAT-04,
 * `ANALISE-CONTROLE-ACESSO.md` §4.3).
 *
 * É a única peça que sabe traduzir o catálogo para o formato que
 * `createMongoAbility` consome. Os `PAPEIS_<Dominio>` (T52, T53) declaram
 * intenção; aqui ela vira condição.
 *
 * **Uma regra por relação, nunca `$or`.** Medido neste repositório em T51 com o
 * `@casl/ability@6.7.1` instalado: `conditions: { $or: [...] }` **nunca casa** —
 * `createMongoAbility` não registra os operadores lógicos, e a condição sai
 * falsa sem erro nenhum. Duas regras com o mesmo par ação/recurso, por outro
 * lado, o CASL avalia em OR nativamente. É o padrão que `rdo.ts` já usa hoje sem
 * saber: duas chamadas `can(...)`, uma por campo de site manager.
 *
 * **Atenuação é concessão condicional, não negação.** O compilador já emite a
 * regra restrita, em vez de conceder tudo e tirar depois. Isso é o que faz a
 * mesma declaração funcionar na checagem por registro (`pode`) e no gate de
 * módulo (`podeNoModulo`, T57): a regra condicional é reconhecível como
 * condicional, e o gate de módulo pode ignorá-la em vez de dar falso positivo
 * (P1-5 do spec, ADR-0003).
 *
 * **O compilador levanta em vez de negar.** Relação inexistente, recurso sem
 * relação declarada, `exigeRelacao: []` e `estados` em recurso sem
 * `CAMPO_ESTADO` são todos erro de catálogo. O caminho fácil seria devolver uma
 * condição impossível — e aí o defeito vira "fulano perdeu o acesso", meses
 * depois, sem rastro. Erro na compilação do catálogo aparece no primeiro teste.
 *
 * **Delegação é identidade a mais na condição, não regra a mais** (T113/T132,
 * DEL-01, `ANALISE-CONTROLE-ACESSO.md` §4.8). A condição de relação usa
 * `$in: identidadesDe(usuario, agora, recurso)`: uma única regra passa a
 * conceder ao titular **e** ao substituto ativo, e nega a terceiros — sem
 * duplicar regra e sem o `$or` que este mesmo CASL nunca casa (nota acima).
 * `escopo.obras` de uma delegação (T132) restringe a identidade delegada a um
 * subconjunto de obras pelo MESMO mecanismo — uma regra por alternativa, não
 * `$or` — via `gruposDeIdentidade`. As duas coisas moram em `./delegacao.ts`
 * (extraído na T132/T143 para este arquivo caber no ratchet de 300 LOC,
 * `docs/regras-migracao.md` §4); aqui só a chamada, em `compilarConcessao`.
 */
import type { Acao, Recurso } from "./catalogo/vocabulario";
import { CAMPO_ESTADO, RELACOES } from "./catalogo/relacoes";
import {
  gruposDeIdentidade,
  identidadesDe,
  menorFimDeDelegacaoAtiva,
  type Delegacao,
  type IdentidadeDoUsuario,
} from "./delegacao";

export type { Delegacao, IdentidadeDoUsuario };
export { identidadesDe, menorFimDeDelegacaoAtiva };

/**
 * Uma regra CASL crua, no formato que `createMongoAbility` aceita.
 *
 * Declarada aqui em vez de importada do `@casl/ability` porque o tipo `RawRule`
 * da lib é genérico sobre a Ability e arrasta a instanciação inteira; o que este
 * módulo produz é sempre a mesma forma.
 */
export interface RegraCompilada {
  action: Acao[];
  subject: Recurso;
  conditions?: Record<string, unknown>;
}

/**
 * `Concessao` como o compilador a lê: só leitura.
 *
 * Os arquivos de `papeis/` declaram o catálogo com `as const`, o que torna cada
 * `acoes` e cada `estados` um array readonly — e `readonly Acao[]` não é
 * atribuível a `Acao[]`. O compilador só lê, então pedir a forma mutável seria
 * exigir do catálogo uma garantia que ele não precisa dar. `Concessao` continua
 * atribuível a esta.
 */
export interface ConcessaoLegivel {
  readonly acoes: readonly Acao[];
  readonly recurso: Recurso;
  readonly exigeRelacao?: readonly string[];
  readonly estados?: readonly string[];
  readonly condicao?: Readonly<Record<string, unknown>>;
}

function ehRecursoComRelacao(recurso: Recurso): recurso is keyof typeof RELACOES {
  return recurso in RELACOES;
}

function ehRecursoComEstado(recurso: Recurso): recurso is keyof typeof CAMPO_ESTADO {
  return recurso in CAMPO_ESTADO;
}

/**
 * Caminho do campo da relação `nome` dentro de um registro de `recurso`.
 *
 * Levanta com os nomes válidos em vez de devolver `undefined`: um caminho
 * `undefined` vira a condição `{ undefined: { $eq: email } }`, que nega tudo em
 * silêncio.
 */
function campoDaRelacao(recurso: Recurso, nome: string): string {
  if (!ehRecursoComRelacao(recurso)) {
    throw new Error(
      `Recurso "${recurso}" nao tem relacao declarada em RELACOES; ` +
        `recursos com relacao: ${Object.keys(RELACOES).join(", ")}`,
    );
  }

  const relacoes: Record<string, { campo: string; rotulo: string }> = RELACOES[recurso];
  const relacao = relacoes[nome];
  if (!relacao) {
    throw new Error(
      `Relacao "${nome}" nao existe em RELACOES.${recurso}; ` +
        `validas: ${Object.keys(relacoes).join(", ")}`,
    );
  }
  return relacao.campo;
}

/**
 * Condição de estado da concessão. Objeto vazio quando a concessão não declara
 * `estados` — o que mantém a regra incondicional, e portanto visível ao gate de
 * módulo.
 */
function condicaoDeEstado(concessao: ConcessaoLegivel): Record<string, unknown> {
  if (!concessao.estados || concessao.estados.length === 0) return {};

  if (!ehRecursoComEstado(concessao.recurso)) {
    throw new Error(
      `Concessao declara "estados" para o recurso "${concessao.recurso}", ` +
        `que nao tem campo de estado em CAMPO_ESTADO; ` +
        `recursos com estado: ${Object.keys(CAMPO_ESTADO).join(", ")}`,
    );
  }

  return { [CAMPO_ESTADO[concessao.recurso]]: { $in: [...concessao.estados] } };
}

/**
 * Traduz uma concessão do catálogo em regras CASL.
 *
 * Sem `exigeRelacao`, é uma regra só. Com `exigeRelacao`, é **uma regra por
 * relação** — o CASL avalia o conjunto em OR, e é assim que "site manager ou
 * suplente" vira política sem `$or`.
 *
 * `estados` e `condicao` compõem com a relação na mesma regra (AND), porque é o
 * que a política imperativa faz hoje: `edit` em Contratos exige ser o gestor
 * **e** o contrato estar em revisão.
 */
export function compilarConcessao(
  concessao: ConcessaoLegivel,
  usuario: IdentidadeDoUsuario,
  agora: Date = new Date(),
): RegraCompilada[] {
  const acoes = [...concessao.acoes];
  const estado = condicaoDeEstado(concessao);
  const extra = concessao.condicao ?? {};

  if (!concessao.exigeRelacao) {
    const conditions = { ...estado, ...extra };
    return [
      {
        action: acoes,
        subject: concessao.recurso,
        ...(Object.keys(conditions).length > 0 ? { conditions } : {}),
      },
    ];
  }

  // `exigeRelacao: []` seria "exige uma de nenhuma relacao", que nao concede a
  // ninguem. Devolver a regra incondicional aqui seria o inverso exato do que a
  // declaracao pede — conceder a todos. Erro de catalogo, e para em teste.
  if (concessao.exigeRelacao.length === 0) {
    throw new Error(
      `Concessao para "${concessao.recurso}" declara "exigeRelacao" vazio; ` +
        `omita o campo para conceder sem relacao`,
    );
  }

  // Uma chamada só: as três regras de relação da mesma concessão avaliam a
  // mesma janela de vigência contra o mesmo `agora`, então recalcular por
  // relação não mudaria o resultado — só repetiria o `filter`.
  //
  // T132: uma regra por (relação × grupo de identidade), não por relação só —
  // é o que separa "próprio usuário, sem restrição" de "titular delegado,
  // restrito às obras do §9" sem depender de `$or` (que este CASL não casa).
  // No caminho comum (nenhuma delegação, ou delegação sem `escopo.obras`) há
  // um grupo só e a contagem de regras não muda — mesma forma de antes da
  // T132.
  const grupos = gruposDeIdentidade(usuario, agora, concessao.recurso);

  return concessao.exigeRelacao.flatMap((nome) =>
    grupos.map((grupo) => ({
      action: acoes,
      subject: concessao.recurso,
      conditions: {
        [campoDaRelacao(concessao.recurso, nome)]: { $in: grupo.emails },
        ...(grupo.condicaoExtra ?? {}),
        ...estado,
        ...extra,
      },
    })),
  );
}
